/**
 * Templates as an organisation keeps them (Stage 3): its own, saved as
 * numbered versions, beside the starters, which never change.
 *
 * - `parseTemplate()` is the gate in front of storage for what the editor
 *   sends. It rebuilds the template from an allow-list, field by field, so
 *   nothing the engine does not know rides into storage (the rule `cardFor()`
 *   keeps for what leaves), then runs `validateTemplate()`.
 * - `offered()` and `offboardingFor()` decide which templates a site starts
 *   from. A copy of a starter replaces that starter wherever the copy is
 *   offered, so an admin who adapts the standard onboarding for one site does
 *   not leave the original beside it to be picked by mistake.
 *
 * A version, once saved, does not change: an instance is pinned to the nodes
 * it was made from (it carries a copy), and moving one to a newer version is
 * `migrate()`'s, opted into per instance with the difference shown first.
 */

import { isBlank } from "./cascade.js";
import { TEMPLATE_LIBRARY, libraryTemplate } from "./library.js";
import { STARTER_TEMPLATES } from "./starters.js";
import {
  ACCESS_CHANGE,
  ANSWERS,
  type Answer,
  type Condition,
  type DaySpec,
  type DeadlineSpec,
  type FieldLink,
  type FieldSpec,
  type OffboardingUse,
  type ProfileKey,
  type Question,
  type Template,
  type TemplateEdge,
  type TemplateNode,
} from "./template.js";
import { validateTemplate } from "./validate.js";

/** Enough for any real template, few enough that a pasted mistake is caught rather than stored. */
export const TEMPLATE_LIMITS = {
  cards: 200,
  questions: 30,
  name: 120,
  title: 160,
  description: 4000,
  line: 300,
  lines: 40,
  fields: 30,
  options: 40,
  sites: 200,
} as const;

/** Where a template comes from: utuwrk's library (the starters among it), or the organisation's own. */
export const isStarter = (id: string): boolean => TEMPLATE_LIBRARY.some((entry) => entry.template.id === id);

/** Which kind of offboarding template a departure runs on. */
export const useFor = (trigger: string): OffboardingUse =>
  trigger === "termination" ? "termination" : trigger === ACCESS_CHANGE ? "access-change" : "standard";

export const useOf = (template: Pick<Template, "kind" | "use">): OffboardingUse | null =>
  template.kind === "offboarding" ? (template.use ?? "standard") : null;

const offeredAt = (template: Pick<Template, "sites">, siteId: string): boolean =>
  !template.sites?.length || template.sites.includes(siteId);

/**
 * The templates a site starts from, of one kind: the organisation's own that
 * are offered there, its site-specific ones first, then each starter that no
 * copy offered there replaces. `own` holds current versions only, and none
 * that were retired.
 */
export function offered(own: readonly Template[], kind: Template["kind"], siteId: string): Template[] {
  const mine = own.filter((template) => template.kind === kind && offeredAt(template, siteId));
  const specific = mine.filter((template) => template.sites?.length);
  const general = mine.filter((template) => !template.sites?.length);
  const replaced = new Set(mine.map((template) => template.basedOn).filter(Boolean));
  const starters = STARTER_TEMPLATES.filter(
    (template) => template.kind === kind && !replaced.has(template.id),
  );
  return [...specific, ...general, ...starters];
}

/**
 * The templates a departure for this reason may run on at this site, the one
 * to use first. Nobody chooses a slow template for a termination: every one
 * offered is for the kind of departure the reason names (`useFor()`).
 */
export const offboardingFor = (own: readonly Template[], trigger: string, siteId: string): Template[] =>
  offered(own, "offboarding", siteId).filter((template) => useOf(template) === useFor(trigger));

/* -------------------------------------------------------------- parsing -- */

type Parsed<T> = { ok: true; value: T } | { ok: false; reason: string };

class Refused extends Error {}
/** Thrown inside the parser only, and caught at its edge: the parse is a walk that stops at the first wrong thing. */
const stop = (reason: string): never => {
  throw new Refused(reason);
};

const record = (value: unknown, what: string): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : stop(`${what} is not written the way a template is.`);

const list = (value: unknown, what: string, max: number): unknown[] => {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return stop(`${what} is not a list.`);
  if (value.length > max) return stop(`${what} has more than ${max} entries.`);
  return value;
};

const text = (value: unknown, what: string, max: number): string => {
  if (typeof value !== "string") return stop(`${what} is missing.`);
  if (value.length > max) return stop(`${what} is longer than ${max} characters.`);
  return value;
};

const optionalText = (value: unknown, what: string, max: number): string | undefined =>
  value === undefined || value === null || value === "" ? undefined : text(value, what, max);

const whole = (value: unknown, what: string): number =>
  typeof value === "number" && Number.isInteger(value) ? value : stop(`${what} is not a whole number.`);

const flag = (value: unknown): true | undefined => (value === true ? true : undefined);

const unit = (value: unknown, what: string): "business" | "calendar" | undefined =>
  value === undefined || value === null
    ? undefined
    : value === "business" || value === "calendar"
      ? value
      : stop(`${what} counts in something other than business or calendar days.`);

/** Copies only the keys that hold something, so an absent option stays absent (`exactOptionalPropertyTypes`). */
function compact<T extends object>(value: { [K in keyof T]-?: T[K] | undefined }): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

function deadline(raw: unknown, name: string): DeadlineSpec {
  const value = record(raw, `The deadline of ${name}`);
  if (value.from === "trigger" && value.minutes !== undefined)
    return { from: "trigger", minutes: whole(value.minutes, `The deadline of ${name}`) };
  if (value.from !== "anchor" && value.from !== "trigger")
    return stop(`The deadline of ${name} is counted from nothing.`);
  return value.from === "anchor"
    ? compact<{ from: "anchor"; days: number; unit?: "business" | "calendar" }>({
        from: "anchor",
        days: whole(value.days, `The deadline of ${name}`),
        unit: unit(value.unit, `The deadline of ${name}`),
      })
    : compact<{ from: "trigger"; days: number; unit?: "business" | "calendar" }>({
        from: "trigger",
        days: whole(value.days, `The deadline of ${name}`),
        unit: unit(value.unit, `The deadline of ${name}`),
      });
}

function daySpec(raw: unknown, name: string): DaySpec | undefined {
  if (raw === undefined || raw === null) return undefined;
  const value = record(raw, `The day ${name} opens`);
  if (value.from !== "anchor" && value.from !== "trigger")
    return stop(`The day ${name} opens is counted from nothing.`);
  return compact<DaySpec>({
    from: value.from,
    days: whole(value.days, `The day ${name} opens`),
    unit: unit(value.unit, `The day ${name} opens`),
  });
}

function link(raw: unknown, what: string): FieldLink {
  const value = record(raw, what);
  return { node: text(value.node, what, 64), field: text(value.field, what, 64) };
}

function condition(raw: unknown, name: string): Condition {
  const value = record(raw, `A condition on ${name}`);
  const op =
    value.op === "in" || value.op === "notIn" ? value.op : stop(`A condition on ${name} has no test.`);
  const values = list(value.values, `A condition on ${name}`, TEMPLATE_LIMITS.options);
  if ("question" in value) {
    const answers = values.map((entry) =>
      ANSWERS.includes(entry as Answer)
        ? (entry as Answer)
        : stop(`A condition on ${name} has an answer that is not yes, no or does not apply.`),
    );
    return { question: text(value.question, `A condition on ${name}`, 64), op, values: answers };
  }
  if ("recorded" in value) {
    const strings = values.map((entry) => text(entry, `A condition on ${name}`, TEMPLATE_LIMITS.line));
    return { recorded: link(value.recorded, `A condition on ${name}`), op, values: strings };
  }
  const plain = values.map((entry) =>
    typeof entry === "boolean" ? entry : text(entry, `A condition on ${name}`, TEMPLATE_LIMITS.line),
  );
  return { field: text(value.field, `A condition on ${name}`, 64) as ProfileKey, op, values: plain };
}

function field(raw: unknown, name: string): FieldSpec {
  const value = record(raw, `A field on ${name}`);
  const options =
    value.options === undefined
      ? undefined
      : list(value.options, `The choices on ${name}`, TEMPLATE_LIMITS.options).map((entry) =>
          text(entry, `A choice on ${name}`, TEMPLATE_LIMITS.line),
        );
  return compact<FieldSpec>({
    key: text(value.key, `A field on ${name}`, 64),
    label: text(value.label, `A field on ${name}`, TEMPLATE_LIMITS.line),
    pii: flag(value.pii),
    required: flag(value.required),
    options,
    multiple: flag(value.multiple),
    issues: optionalText(value.issues, `What ${name} issues`, 40),
    returns: optionalText(value.returns, `What ${name} collects`, 40),
  });
}

function node(raw: unknown, index: number): TemplateNode {
  const value = record(raw, `Card ${index + 1}`);
  const name =
    typeof value.title === "string" && value.title.trim() ? `"${value.title.trim()}"` : `card ${index + 1}`;
  const kind =
    value.kind === "task" || value.kind === "notify"
      ? value.kind
      : stop(`${name} is neither a task nor a notice.`);
  const strings = (entry: unknown, what: string) =>
    entry === undefined
      ? undefined
      : list(entry, what, TEMPLATE_LIMITS.lines).map((line) => text(line, what, TEMPLATE_LIMITS.line));
  const fields =
    value.fields === undefined
      ? undefined
      : list(value.fields, `The fields on ${name}`, TEMPLATE_LIMITS.fields).map((entry) =>
          field(entry, name),
        );
  const inputs =
    value.inputs === undefined
      ? undefined
      : list(value.inputs, `What ${name} reads`, TEMPLATE_LIMITS.fields).map((entry) =>
          link(entry, `What ${name} reads`),
        );
  const conditions =
    value.conditions === undefined
      ? undefined
      : list(value.conditions, `The conditions on ${name}`, TEMPLATE_LIMITS.lines).map((entry) =>
          condition(entry, name),
        );
  return compact<TemplateNode>({
    key: text(value.key, `The key of ${name}`, 64),
    kind,
    title: text(value.title, `The title of ${name}`, TEMPLATE_LIMITS.title),
    description: optionalText(value.description, `The description of ${name}`, TEMPLATE_LIMITS.description),
    department: text(value.department, `The department of ${name}`, 80),
    deadline: deadline(value.deadline, name),
    subtasks: strings(value.subtasks, `The checklist of ${name}`),
    fields,
    inputs,
    checklistFrom:
      value.checklistFrom === undefined ? undefined : link(value.checklistFrom, `The checklist of ${name}`),
    conditions,
    timeCritical: flag(value.timeCritical),
    revokesAccess: flag(value.revokesAccess),
    compliance: flag(value.compliance),
    finalPay: flag(value.finalPay),
    signoffs: strings(value.signoffs, `The sign-offs on ${name}`),
    notes: flag(value.notes),
    urgency: value.urgency === "urgent" ? "urgent" : undefined,
    link: optionalText(value.link, `The link on ${name}`, 2000),
    mailTo: optionalText(value.mailTo, `The address ${name} mails`, 254),
    notBefore: daySpec(value.notBefore, name),
    gate: flag(value.gate),
  });
}

function edge(raw: unknown): TemplateEdge {
  const value = record(raw, "A dependency");
  const kind =
    value.kind === "hard" || value.kind === "soft"
      ? value.kind
      : stop("A dependency is neither hard nor soft.");
  return { from: text(value.from, "A dependency", 64), to: text(value.to, "A dependency", 64), kind };
}

function question(raw: unknown): Question {
  const value = record(raw, "A question");
  return compact<Question>({
    key: text(value.key, "A question", 64),
    text: text(value.text, "A question", TEMPLATE_LIMITS.line),
    help: optionalText(value.help, "A question's help", TEMPLATE_LIMITS.line),
  });
}

/**
 * What the editor sent, as a template the engine can run, or the first thing
 * wrong with it as a sentence. `id`, `version` and `basedOn` are the store's
 * to set, never the sender's: they come from `identity`.
 */
export function parseTemplate(
  input: unknown,
  identity: { id: string; version: number; basedOn?: string },
  siteIds: readonly string[],
): Parsed<Template> {
  try {
    const raw = record(input, "The template");
    const kind =
      raw.kind === "onboarding" || raw.kind === "offboarding"
        ? raw.kind
        : stop("A template is either an onboarding or an offboarding.");
    const use =
      kind === "offboarding"
        ? raw.use === undefined || raw.use === "standard"
          ? "standard"
          : raw.use === "termination" || raw.use === "access-change"
            ? raw.use
            : stop("An offboarding template is for a standard departure, a termination or an access change.")
        : undefined;
    const sites = list(raw.sites, "The sites", TEMPLATE_LIMITS.sites).map((entry) =>
      typeof entry === "string" && siteIds.includes(entry)
        ? entry
        : stop("One of the sites it is offered at no longer exists."),
    );
    const nodes = list(raw.nodes, "The cards", TEMPLATE_LIMITS.cards).map(node);
    const template: Template = compact<Template>({
      id: identity.id,
      name: text(raw.name, "The name", TEMPLATE_LIMITS.name).trim(),
      version: identity.version,
      kind,
      use,
      sites: sites.length ? [...new Set(sites)] : undefined,
      basedOn: identity.basedOn,
      questions:
        raw.questions === undefined
          ? undefined
          : list(raw.questions, "The questions", TEMPLATE_LIMITS.questions).map(question),
      nodes,
      edges: list(raw.edges, "The dependencies", TEMPLATE_LIMITS.cards * 4).map(edge),
    });
    const problems = validateTemplate(template);
    if (problems.length === 1) return { ok: false, reason: problems[0]! };
    if (problems.length > 1)
      return {
        ok: false,
        reason: `${problems[0]} There are ${problems.length - 1} more things to fix; the editor lists them.`,
      };
    return { ok: true, value: template };
  } catch (failure) {
    if (failure instanceof Refused) return { ok: false, reason: failure.message };
    throw failure;
  }
}

/* ----------------------------------------------------------- files -- */

/** What a template file says it is, so a stray JSON file is told apart from one. */
export const TEMPLATE_FILE_FORMAT = "utuwrk.template";

/** The most templates one imported file may hold. */
export const IMPORT_MAX_TEMPLATES = 50;

/**
 * One template as a file to keep or hand to another organisation: readable
 * JSON, the template whole. Its sites are left out, since they are this
 * organisation's and mean nothing anywhere else.
 */
export function templateFile(template: Template, exportedAt: number): string {
  const { sites: _sites, ...portable } = template;
  return `${JSON.stringify(
    {
      format: TEMPLATE_FILE_FORMAT,
      formatVersion: 1,
      exportedAt: new Date(exportedAt).toISOString(),
      template: portable,
    },
    null,
    2,
  )}
`;
}

/**
 * The templates in a file somebody chose to import: a template file
 * (`templateFile()`), a template written out bare, or Export data's
 * Templates file, from which each template's latest version is taken. Each
 * comes back as it was written; `parseTemplate()` decides whether it is one.
 */
export function templatesInFile(input: unknown): Parsed<unknown[]> {
  const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);
  if (!isObject(input))
    return { ok: false, reason: "That file is not a template. Choose a .json file exported from utuwrk." };
  let found: unknown[];
  if (input.format === TEMPLATE_FILE_FORMAT) found = [input.template];
  else if (Array.isArray(input.nodes)) found = [input];
  else if (Array.isArray(input.templates))
    found = input.templates.map((entry) => {
      const versions = isObject(entry) && Array.isArray(entry.versions) ? entry.versions : [];
      const latest = versions[versions.length - 1];
      return isObject(latest) ? latest.template : entry;
    });
  else return { ok: false, reason: "That file is not a template. Choose a .json file exported from utuwrk." };
  if (found.length === 0) return { ok: false, reason: "That file has no templates in it." };
  if (found.length > IMPORT_MAX_TEMPLATES)
    return {
      ok: false,
      reason: `That file has more than ${IMPORT_MAX_TEMPLATES} templates. Import them a few at a time.`,
    };
  return { ok: true, value: found };
}

/**
 * Which starter an imported template takes the place of, as a copy made here
 * would: a template from utuwrk's library, exported as it is, takes the place
 * of the starter it is a variant of; one copied from a starter elsewhere,
 * of that starter. Anything else takes nobody's place.
 */
export function importedBasedOn(raw: unknown): string | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const { id, basedOn } = raw as { id?: unknown; basedOn?: unknown };
  for (const candidate of [id, basedOn]) {
    if (typeof candidate !== "string" || !isStarter(candidate)) continue;
    return libraryTemplate(candidate)?.basedOn ?? candidate;
  }
  return undefined;
}

/** A name for a copy that does not read as the original. */
export const copyName = (name: string): string => (isBlank(name) ? "New template" : `${name} (copy)`);
